import React, { useEffect, useRef } from 'react';

/** Modal panel: a bottom sheet on phones; on desktop a right-hand drawer (`side="right"`)
 *  or a centred dialog (`side="center"`). Escape / backdrop close, body scroll lock,
 *  and focus moves in on open and back to the trigger on close. */
export default function Sheet({ open, onClose, title, side = 'right', children, footer, labelledBy }) {
  const panelRef = useRef(null);
  const lastFocus = useRef(null);
  // Callers pass inline handlers; read the latest through a ref so the open/close effect
  // runs once per opening, not on every parent render (which would steal focus).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    lastFocus.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => panelRef.current?.querySelector('button, a, input, select')?.focus(), 30);
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      lastFocus.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="rt-sheet-root">
      <div className="rt-sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div ref={panelRef} className={`rt-sheet rt-sheet--${side}`} role="dialog" aria-modal="true"
        aria-label={labelledBy ? undefined : title} aria-labelledby={labelledBy}>
        <div className="rt-sheet-head">
          <span className="rt-sheet-grip" aria-hidden="true" />
          {title && <h2 className="rt-sheet-title">{title}</h2>}
          <button type="button" className="rt-icon-btn" aria-label="Close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
          </button>
        </div>
        <div className="rt-sheet-body">{children}</div>
        {footer && <div className="rt-sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}
